import { useState, useEffect } from 'react';
import { Calendar, User, Building2, CheckCircle, Clock, AlertTriangle, FileText, Search } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Inspection {
  id: string;
  property_id: string;
  inspector_id: string | null;
  inspection_date: string;
  status: string;
  completion_percentage: number;
  created_at: string;
  properties: {
    name: string;
    address: string;
  };
  maintenance_personnel: {
    name: string;
  } | null;
}

interface InspectionListProps {
  onSelectInspection: (inspectionId: string) => void;
  onViewReport: (inspectionId: string) => void;
  refreshTrigger?: number;
}

export function InspectionList({ onSelectInspection, onViewReport, refreshTrigger }: InspectionListProps) {
  const [inspections, setInspections] = useState<Inspection[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadInspections();
  }, [refreshTrigger]);

  const loadInspections = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('inspections')
        .select(`
          *,
          properties(name, address),
          maintenance_personnel(name)
        `)
        .order('inspection_date', { ascending: false });

      if (error) throw error;
      setInspections(data || []);
    } catch (err) {
      console.error('Error loading inspections:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, { bg: string; text: string; icon: JSX.Element }> = {
      draft: {
        bg: 'bg-gray-100',
        text: 'text-gray-700',
        icon: <FileText size={14} />,
      },
      in_progress: {
        bg: 'bg-blue-100',
        text: 'text-blue-700',
        icon: <Clock size={14} />,
      },
      completed: {
        bg: 'bg-green-100',
        text: 'text-green-700',
        icon: <CheckCircle size={14} />,
      },
      requires_action: {
        bg: 'bg-red-100',
        text: 'text-red-700',
        icon: <AlertTriangle size={14} />,
      },
    };

    const style = styles[status] || styles.draft;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium ${style.bg} ${style.text}`}>
        {style.icon}
        {status.replace('_', ' ').toUpperCase()}
      </span>
    );
  };

  const filteredInspections = inspections.filter((inspection) => {
    const matchesSearch =
      inspection.properties.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.properties.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
      inspection.maintenance_personnel?.name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || inspection.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return <div className="text-center py-8 text-gray-600">Loading inspections...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 mb-6">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search by property or inspector..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="requires_action">Requires Action</option>
        </select>
      </div>

      {filteredInspections.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileText className="mx-auto mb-3 text-gray-400" size={48} />
          <p>No inspections found</p>
          <p className="text-sm mt-1">Create a new inspection to get started</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {filteredInspections.map((inspection) => (
            <div
              key={inspection.id}
              className="bg-white border border-gray-200 rounded-lg p-5 hover:border-blue-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() =>
                inspection.status === 'completed'
                  ? onViewReport(inspection.id)
                  : onSelectInspection(inspection.id)
              }
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3 mb-2">
                    <Building2 className="text-blue-600 flex-shrink-0" size={20} />
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-800 truncate">
                        {inspection.properties.name}
                      </h3>
                      <p className="text-sm text-gray-600 truncate">{inspection.properties.address}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 mt-3 text-sm text-gray-600">
                    <div className="flex items-center gap-1">
                      <Calendar size={16} />
                      <span>{new Date(inspection.inspection_date).toLocaleDateString()}</span>
                    </div>
                    {inspection.maintenance_personnel && (
                      <div className="flex items-center gap-1">
                        <User size={16} />
                        <span>{inspection.maintenance_personnel.name}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2">
                  {getStatusBadge(inspection.status)}
                  <div className="text-right">
                    <div className="text-sm font-semibold text-gray-800">
                      {inspection.completion_percentage}%
                    </div>
                    <div className="text-xs text-gray-500">Complete</div>
                  </div>
                  <div className="w-32 bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all"
                      style={{ width: `${inspection.completion_percentage}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
